/**
 * Gemeinsame Statistik-Bausteine (V-16, Leitlinie 3 und 8): Rahmen mit Frage,
 * Kennzahl-Kachel, waagerechte Balkenliste und kurze Texterkenntnis. Farben
 * nur aus dem zentralen Farbsystem; jeder Wert steht auch als Text da.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils/cn';

/** Rahmen einer Auswertung - die Ueberschrift beantwortet eine Frage. */
export function StatKarte({ titel, frage, aktion, className, children }: {
  titel: string;
  frage?: string;
  /** Rechts in der Kopfzeile, z. B. ein Link zur Statistik-Seite. */
  aktion?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={titel} className={cn('flex min-w-0 flex-col gap-2.5 rounded-lg border border-line bg-surface px-4 py-3.5', className)}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-bold">{titel}</h2>
          {frage && <p className="text-[12px] text-ink-muted">{frage}</p>}
        </div>
        {aktion}
      </div>
      {children}
    </section>
  );
}

/** Eine Zahl mit Beschriftung. */
export function Kennzahl({ label, wert, hinweis, href }: { label: string; wert: string; hinweis?: string; href?: string }) {
  const inhalt = (
    <>
      <span className="beschriftung text-ink-faint">{label}</span>
      <span className="text-[22px] font-extrabold leading-tight tabular-nums">{wert}</span>
      {hinweis && <span className="text-[11px] text-ink-faint">{hinweis}</span>}
    </>
  );
  const klasse = 'flex min-w-0 flex-col rounded-md bg-surface-raised/60 px-2.5 py-2';
  return href ? <Link to={href} className={cn(klasse, 'hover:bg-surface-raised')}>{inhalt}</Link> : <div className={klasse}>{inhalt}</div>;
}

/** Kennzahlen nebeneinander - auf dem Handy zwei, auf dem Laptop bis zu vier. */
export function KennzahlZeile({ children, spalten = 4 }: { children: ReactNode; spalten?: 2 | 3 | 4 }) {
  return <div className={cn('grid grid-cols-2 gap-2', spalten === 3 && 'sm:grid-cols-3', spalten === 4 && 'sm:grid-cols-4')}>{children}</div>;
}

/** Kurze, rein rechnerische Erkenntnis unter einem Diagramm - keine Ratschlaege. */
export function Erkenntnis({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <p className="border-l-2 border-accent/60 pl-2 text-[12px] text-ink-muted">{children}</p>;
}

export interface Balken {
  key: string;
  name: string;
  /** `null` = keine Daten (neutral, nicht als 0). */
  wert: number | null;
  /** Text neben dem Balken; ohne steht der Wert. */
  text?: string;
  /** CSS-Farbe aus den Tokens; ohne der Akzent. */
  farbe?: string;
  /** Kleiner Punkt vor dem Namen, z. B. die Bereichsfarbe (nie ohne Namen, ADR 0003). */
  punkt?: string;
  href?: string;
}

/**
 * Waagerechte Balken mit Namen und Wert - fuer Verteilungen und Vergleiche
 * (Prioritaetsfelder, Projektfortschritt, Quote je Wochentag).
 */
export function BalkenListe({ label, balken, max, format = (v) => v.toLocaleString('de-DE', { maximumFractionDigits: 1 }) }: {
  label: string;
  balken: Balken[];
  /** Ende der Skala; ohne der groesste Wert. */
  max?: number;
  format?: (v: number) => string;
}) {
  const ende = max ?? Math.max(0, ...balken.map((b) => b.wert ?? 0));
  return (
    <ul aria-label={label} className="flex flex-col gap-1.5">
      {balken.map((b) => {
        const text = b.wert === null ? 'keine Daten' : b.text ?? format(b.wert);
        const name = (
          <span className="flex min-w-0 items-center gap-1.5 truncate">
            {b.punkt && <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: b.punkt }} aria-hidden="true" />}
            <span className="truncate">{b.name}</span>
          </span>
        );
        return (
          <li key={b.key} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2 text-[12px]" aria-label={`${b.name}: ${text}`}>
            {b.href ? <Link to={b.href} className="min-w-0 hover:text-accent-text">{name}</Link> : name}
            <span className={cn('h-2.5 overflow-hidden rounded-full', b.wert === null ? 'bg-line/35' : 'bg-surface-raised')} title={`${b.name}: ${text}`}>
              {b.wert !== null && ende > 0 && (
                <span className="block h-full rounded-full" style={{ width: `${Math.min(100, (b.wert / ende) * 100)}%`, backgroundColor: b.farbe ?? 'var(--color-accent)' }} />
              )}
            </span>
            <span className={cn('tabular-nums', b.wert === null ? 'text-ink-faint' : 'text-ink-muted')}>{text}</span>
          </li>
        );
      })}
    </ul>
  );
}
